import type {
  AuthAwareCta,
  ClosingSection,
  FaqSection,
  HowItWorksSection,
  LandingImage,
  LandingFooter,
  LandingQuote,
  NavigationCopy,
  PillarsSection,
  PricingSection,
} from "./types";

/**
 * The advertised seat price, used by every landing page. Must match the
 * Stripe Price that `stripe_subscription_price_id` points at, and the app's
 * own checkout copy (rl-f0ry moved all three to £39 on 2026-10-11).
 */
export const SEAT_PRICE = "£39";

/** The formal introductory offer (hero and closing band, GitHub #383/#414). */
export const firstMonthOffer = `25% off your first month. ${SEAT_PRICE} a month after that. Cancel anytime.`;

export const navigation = {
  brand: "RevisionLab",
  links: [
    { label: "How it works", to: "/how-it-works" },
    { label: "For parents", to: "/for-parents" },
    { label: "Pricing", to: "/pricing" },
    { label: "FAQs", to: "/ai-faqs" },
    { label: "Our story", to: "/our-story" },
  ],
  guest: {
    login: { label: "Log in", to: "/login" },
    primary: { label: "Start your trial", to: "/register" },
  },
  signedIn: {
    primary: { label: "Account", to: "/account" },
  },
} as const satisfies NavigationCopy;

/** The trial is the existing account creation; nothing new behind it. */
export const startTrial = {
  guest: { label: "Start your trial", to: "/register" },
  signedIn: { label: "Manage your account", to: "/account" },
} as const satisfies AuthAwareCta;

export const topicTreeImage = {
  image: "/product/topic-progress.png",
  alt: "RevisionLab biology topics grouped by specification, with progress rings, question counts and a Revise button beside each topic.",
  width: 1138,
  height: 842,
  caption: "Demonstration data",
} as const satisfies LandingImage;

export const markedFeedbackImage = {
  image: "/product/marked-feedback.png",
  alt: "Marked biology feedback awarding two of three marks, showing two credited points about photosynthesis, a missing limiting factor and guidance for the next mark.",
  width: 1438,
  height: 784,
  caption: "Demonstration data",
} as const satisfies LandingImage;

/**
 * GitHub #412: shown in its own section, beside the home page's opening story
 * and beside step 2 of the journey on How it works.
 */
export const amyQuote = {
  text: "It's a really great revision tool and definitely helpful as an A-Level student.",
  attribution: "Amy L, Year 13",
} as const satisfies LandingQuote;

/** GitHub #387: the same journey on Home and How it works (which adds Amy's quote). */
export const practiceJourney = {
  kind: "howItWorks",
  copy: {
    title: "Three steps. One clear result.",
    steps: [
      {
        number: "1",
        title: "Try an exam-style question.",
        body: "Pick a subject and topic and answer in your own words, before you see any feedback.",
        image: topicTreeImage,
      },
      {
        number: "2",
        title: "See where the marks went.",
        body: "Feedback uses the course specification and the official mark scheme to show which marks you earned, which you missed, and why.",
        image: {
          image: "/product/practice-question.png",
          alt: "A RevisionLab biology question about light intensity and photosynthesis, with a typed answer and the Mark my answer button.",
          width: 936,
          height: 984,
          caption: "Demonstration data",
        },
      },
      {
        number: "3",
        title: "Know what to practise next.",
        body: "Progress rings for each topic show strengths and weak spots, and Revisionlab suggests where to focus.",
        image: markedFeedbackImage,
      },
    ],
    cta: startTrial,
  },
} as const satisfies HowItWorksSection;

/** GitHub #389: authoritative marking copy, shared with How it works. */
export const fairMarkingSection = {
  kind: "pillars",
  copy: {
    title: "Marked the way an examiner would.",
    items: [
      {
        title: "Marks that add up.",
        body: "Every question comes with a mark scheme whose marks add up to the question's total.",
      },
      {
        title: "Never a past paper, word for word.",
        body: "Questions are written fresh for each topic.",
      },
      {
        title: "Ask again if it looks wrong.",
        body: "If a mark looks off, your teen can ask for a re-mark, up to three times per question.",
      },
    ],
    image: markedFeedbackImage,
  },
} as const satisfies PillarsSection;

/** GitHub #411: the one plan card, on the homepage and the pricing page. */
export const pricingSection = {
  kind: "pricing",
  copy: {
    title: "One plan. Everything included.",
    amount: SEAT_PRICE,
    period: "a month per seat",
    features: [
      "Exam practice, tutor mode, timed mock exams and focus sessions.",
      "Parent and student each have their own login.",
      "Switch between supported subjects.",
      "Cancel anytime from your account.",
    ],
    offer: "25% off your first month",
    cta: startTrial,
  },
} as const satisfies PricingSection;

/**
 * GitHub #413's questions, worded exactly as given because they answer search
 * terms. The FAQ page lists all seven before the questions it keeps; the
 * homepage shows the first five.
 */
export const parentFaqs = [
  {
    question: "What can my teen do on Revisionlab?",
    answer: "Four things. They can practise exam-style questions marked against the official mark scheme, ask questions and get extra practice in tutor mode, sit timed mock exams, and build up concentration with short focus sessions. Parent and student each have their own login.",
  },
  {
    question: "Is AI marking accurate for GCSE and A-level?",
    answer: "Revisionlab's AI is given the course specification and the official mark scheme for each question, and it explains every mark earned and missed. Marks always add up to the question total. If a mark looks wrong, your teen can ask for a re-mark up to three times per question.",
  },
  {
    question: "How much does a private tutor cost, and what are the alternatives?",
    answer: "The Tutors' Association's 2024 survey puts private tutors at £35-50 an hour for GCSE and £40-85 for A-level, so a weekly tutor costs roughly £140-340 a month. Revisionlab is £39 a month and available every day, with marked feedback on exam answers. It is practice and feedback, not live teaching.",
  },
  {
    question: "How can my teenager improve their exam technique?",
    answer: "Practise answering exam-style questions in their own words, then compare each answer with the mark scheme to see exactly where marks were lost. Short, regular sessions work better than long ones. Revisionlab does this for each topic and shows which topics need the most practice next.",
  },
  {
    question: "Will school see AI revision as cheating?",
    answer: "Revisionlab is built for revision, not for handing in work. Your teen answers each question first, in their own words, then gets feedback. It does not write their answers for them. For any assessed work, check your school's policy on AI.",
  },
  {
    question: "What is the best way for parents to support GCSE & Alevel revision?",
    answer: "Help your teen start rather than supervising every minute: agree short, regular sessions, a quiet place and a clear topic each time. Ask what they found hard rather than how long they studied. Revisionlab's guardian overview, coming soon, will show time spent and topic progress without revealing their answers.",
  },
  {
    question: "Can my teen try it with a discount before I pay?",
    answer: "Yes! RevisionLab is only £39/mo for everything, and your first month is 25% off.",
  },
] as const satisfies FaqSection["copy"]["items"];

/** GitHub #414: logo, one line, and the page links before legal and support. */
export const footer = {
  brand: "RevisionLab",
  microcopy: "UK GCSE and A-level exam practice. Built in the UK.",
  links: navigation.links,
} as const satisfies LandingFooter;

export const liveIndexable = {
  status: "live",
  indexable: true,
  inSitemap: true,
} as const;

/**
 * The same ending on every page (GitHub #414's closing copy), so no page
 * stops on its last section with nowhere to go.
 */
export const closingSection = {
  kind: "closing",
  copy: {
    title: "Ready to see where the marks go?",
    body: "Pick a subject, try a question, get the feedback.",
    cta: startTrial,
    ctaMicrocopy: firstMonthOffer,
  },
} as const satisfies ClosingSection;
